import { useMemo } from "react"

import { cn } from "@/lib/utils"
import { useChat } from "@/features/chat"

import ChevronRight from "@/assets/svg/compatibility/chevron-right.svg?react"
import TapTip from "@/assets/svg/today/biorhythm/tap_tip_green.svg?react"

import { REPORTS, type ReportId, type ReportItem } from "../config/reports-data"
import { isPurchasableReport } from "../config/report-plans"
import { useReportPurchaseStore } from "../hooks/report-purchase-store"
import { useReportCta } from "../hooks/use-report-cta"
import { useReportStates } from "../hooks/use-report-states"
import { useReportUnlock } from "../hooks/use-report-unlock"
import { useTabReportTimerLabel } from "../hooks/use-tab-report-timer-label"
import { useUserReports } from "../hooks/use-user-reports"
import { ReportGetButtonCompact } from "./reports/report-get-button"
import { ReportUnlockingCard } from "./reports/report-unlocking-card"

interface PersonalizedReportsProps {
  /** Hide these catalog rows (e.g. the report for the screen you're already on). */
  excludeIds?: readonly ReportId[]
}

/** Compact "Personalized Reports" cross-sell list. Ported from Today/common/PersonalizedReports. */
export function PersonalizedReports({ excludeIds }: PersonalizedReportsProps) {
  return (
    <div className="mt-10 px-4">
      <h3 className="mb-4 font-display text-2xl font-medium text-purple-light">
        Personalized Reports
      </h3>

      <PersonalizedReportsCard excludeIds={excludeIds} />
    </div>
  )
}

/**
 * The offers card on its own (rows + "Get" tip), without the "Personalized Reports"
 * heading. Reused by the My Plan screen under a different intro.
 */
interface ReportRowState {
  report: ReportItem
  purchased: boolean
  inProgress: boolean
  price: number
}

/**
 * List order for the reports card: a report that's been purchased and whose
 * unlock timer has finished (an owned star/truths report, no longer processing)
 * drops to the end of the list. Everything else keeps its catalog order — the
 * still-buyable reports, the one currently processing (stays put with its
 * timer), and the always-available in-app reports. Stable within each group.
 */
function orderReports(rows: ReportRowState[]): ReportRowState[] {
  const isDone = (r: ReportRowState) =>
    isPurchasableReport(r.report.purchaseType) && r.purchased && !r.inProgress
  return [...rows.filter((r) => !isDone(r)), ...rows.filter(isDone)]
}

export function PersonalizedReportsCard({
  className,
  excludeIds,
}: {
  className?: string
  excludeIds?: readonly ReportId[]
}) {
  const { userMainSubscriptionId } = useChat()
  const { handleReportCta, isReportPurchasing } = useReportCta({ userMainSubscriptionId })

  // Row state comes from the backend list; re-sort when it changes (e.g. a report
  // flips processing → ready after the estimate-elapse refetch).
  const { reports: apiReports } = useUserReports()
  const stateOf = useReportStates()

  const excludeKey = excludeIds?.join("\0") ?? ""
  const reports = useMemo(() => {
    const excluded = excludeIds ? new Set(excludeIds) : null
    const rows = REPORTS.filter((report) => !excluded?.has(report.id)).map((report) => ({
      report,
      ...stateOf(report),
    }))
    return orderReports(rows)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiReports, stateOf, excludeKey])

  // Same ownership source as ReportsCategory (`useReportStates` → `purchased`).
  const allReportsPurchased = useMemo(
    () => REPORTS.every((report) => stateOf(report).purchased),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiReports, stateOf],
  )

  return (
    <div
      className={cn(
        "flex flex-col gap-4 rounded-3xl bg-dark-base p-4 shadow-[0_0_16px_rgba(255,83,127,0.2),0_0_32px_#9565FD]",
        className,
      )}
    >
      <div className="flex flex-col gap-1">
        {reports.map(({ report, purchased, price }) => (
          <Row
            key={report.id}
            report={report}
            purchased={purchased}
            price={price}
            isPurchasing={isReportPurchasing(report)}
            onCta={() => handleReportCta(report, purchased)}
          />
        ))}
      </div>

      {!allReportsPurchased && (
        <div className="flex items-center gap-2">
          <TapTip className="h-[42px] w-[35px] shrink-0" />
          <p className="min-w-0 flex-1 font-body text-sm text-purple-light/70">
            Tap <span className="font-medium text-purple-light">“Get”</span> to purchase a report at
            the displayed price using your saved payment method
          </p>
        </div>
      )}
    </div>
  )
}

function Row({
  report,
  purchased,
  price,
  isPurchasing,
  onCta,
}: {
  report: ReportItem
  purchased: boolean
  price: number
  isPurchasing: boolean
  onCta: () => void
}) {
  const purchaseUnlock = useReportUnlock(report.id)
  const tabTimerLabel = useTabReportTimerLabel(report.id)
  const inProgress = purchaseUnlock.inProgress || tabTimerLabel !== null
  const remainingLabel = tabTimerLabel ?? purchaseUnlock.remainingLabel
  const showSheet = useReportPurchaseStore((s) => s.showSheet)

  // While the post-purchase unlock window runs (or the tabbar lock timer for
  // palmistry / compatibility), the row becomes the "in progress" card.
  // Tap re-opens the shared "Report in Progress" sheet for either timer source.
  if (inProgress) {
    return (
      <button
        type="button"
        onClick={() => showSheet(report)}
        className="w-full text-left"
      >
        <ReportUnlockingCard report={report} remainingLabel={remainingLabel} dimmed compact />
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={onCta}
      disabled={isPurchasing}
      className="relative flex h-[54px] items-center overflow-hidden rounded-xl bg-gradient-to-b from-purple-light/10 to-purple-light/10 bg-dark-base pr-3 text-left disabled:opacity-80"
    >
      {!purchased && (
        <span className="absolute bottom-0 left-0 flex h-4 w-10 items-center justify-center rounded-tr border border-[#38295E] bg-[#8459E3] font-body text-[8px] font-bold text-white">
          ${price}
        </span>
      )}

      <img src={report.cover} alt={report.title} className="h-[54px] w-20 shrink-0 object-cover" />

      <span className="min-w-0 flex-1 px-3 font-display text-base font-medium text-white">
        {report.title}
      </span>

      {purchased ? (
        <span className="flex size-6 shrink-0 items-center justify-center text-purple-light/70 [&_svg]:w-[7px]">
          <ChevronRight />
        </span>
      ) : (
        <ReportGetButtonCompact isLoading={isPurchasing} />
      )}
    </button>
  )
}
